import React, { useState, useEffect } from 'react';
// Se ha añadido 'Coffee' a las importaciones para solucionar el ReferenceError
import { AlertTriangle, User, Weight, Calendar, Baby, ChevronDown, Coffee } from 'lucide-react';

const App = () => {
  // --- Estados iniciales (permitimos strings para facilitar la edición en los inputs) ---
  const [weight, setWeight] = useState('70');
  const [age, setAge] = useState('30');
  const [sex, setSex] = useState('female');
  const [isPregnant, setIsPregnant] = useState(false);
  const [coffeeType, setCoffeeType] = useState('espresso');
  const [result, setResult] = useState(null);

  // --- Datos de cafeína (mg por porción estándar) ---
  const coffeeData = {
    espresso: { name: 'Espresso (30ml)', mg: 63 },
    americano: { name: 'Filtrado / Americano (240ml)', mg: 95 },
    instant: { name: 'Café Soluble (1 cdta)', mg: 60 },
    coldbrew: { name: 'Cold Brew (240ml)', mg: 155 },
    frenchpress: { name: 'Prensa Francesa (240ml)', mg: 110 },
  };

  /**
   * Lógica de cálculo basada en las directrices de la FDA y la EFSA.
   * Calcula el límite seguro diario de cafeína basado en el perfil del usuario.
   */
  const calculateCaffeine = () => {
    // Convertimos a número para el cálculo, si está vacío usamos 0
    const numWeight = parseFloat(weight) || 0;
    const numAge = parseInt(age) || 0;

    if (numWeight === 0) {
      setResult(null);
      return;
    }

    let limit = 400; // Límite estándar para adultos sanos (mg)

    // Ajuste basado en el peso (aprox. 5.7 mg por kg, con un tope de 400 mg)
    const weightBasedLimit = numWeight * 5.7;
    limit = Math.min(400, weightBasedLimit);

    // Ajuste para menores de 18 años (Límite recomendado: 2.5 mg/kg)
    if (numAge < 18 && numAge > 0) {
      limit = numWeight * 2.5;
    }

    // Ajuste por embarazo (Límite estricto de 200 mg)
    if (isPregnant && sex === 'female') {
      limit = 200;
    }

    // Ajuste por edad avanzada (El metabolismo se ralentiza un 20%)
    if (numAge > 65) {
      limit = limit * 0.8;
    }

    const caffeinePerCup = coffeeData[coffeeType].mg;
    const cupsPerDay = limit / caffeinePerCup;

    setResult({
      dailyLimit: Math.round(limit),
      cups: cupsPerDay.toFixed(1),
      coffeeName: coffeeData[coffeeType].name
    });
  };

  // Recalcular el resultado cada vez que cambie una variable de entrada
  useEffect(() => {
    calculateCaffeine();
  }, [weight, age, sex, isPregnant, coffeeType]);

  // Manejador para evitar el cero a la izquierda al borrar
  const handleInputChange = (setter) => (e) => {
    const val = e.target.value;
    // Si el usuario borra todo, dejamos el string vacío en lugar de un 0
    setter(val);
  };

  return (
    <div className="min-h-screen bg-[#fdfaf6] p-4 md:p-8 font-sans text-stone-800 flex items-center justify-center">
      <div className="max-w-2xl w-full bg-white rounded-3xl shadow-xl overflow-hidden border border-stone-100">
        
        {/* Cabecera con el Logo de 60 Granos */}
        <div className="bg-[#3d2b1f] p-8 text-center text-white relative">
          <div className="flex justify-center mb-2">
            <img 
              src="/60granos-logo.png" 
              alt="Logo 60 Granos Café" 
              className="h-32 w-auto object-contain brightness-0 invert opacity-90 transition-opacity hover:opacity-100"
              onError={(e) => {
                e.target.onerror = null; 
                e.target.src = "https://via.placeholder.com/200x120?text=60+Granos+Café";
              }}
            />
          </div>
          <p className="text-[#d4a373] font-medium uppercase tracking-[0.3em] text-[10px] mt-2">
            Calculadora de Cafeína
          </p>
          <p className="mt-4 text-stone-400 text-xs max-w-md mx-auto italic">
            "Tu dosis perfecta para disfrutar el café con bienestar."
          </p>
        </div>

        <div className="p-6 md:p-10 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Entrada: Peso Corporal */}
            <div className="space-y-2">
              <label className="flex items-center text-[10px] font-bold text-stone-500 gap-2 uppercase tracking-widest">
                <Weight size={14} /> Peso Corporal (kg)
              </label>
              <input 
                type="number" 
                value={weight} 
                onChange={handleInputChange(setWeight)}
                placeholder="0"
                className="w-full p-3 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#d4a373] outline-none transition-all font-semibold"
              />
            </div>

            {/* Entrada: Edad */}
            <div className="space-y-2">
              <label className="flex items-center text-[10px] font-bold text-stone-500 gap-2 uppercase tracking-widest">
                <Calendar size={14} /> Tu Edad
              </label>
              <input 
                type="number" 
                value={age} 
                onChange={handleInputChange(setAge)}
                placeholder="0"
                className="w-full p-3 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#d4a373] outline-none transition-all font-semibold"
              />
            </div>

            {/* Entrada: Sexo */}
            <div className="space-y-2">
              <label className="flex items-center text-[10px] font-bold text-stone-500 gap-2 uppercase tracking-widest">
                <User size={14} /> Sexo
              </label>
              <div className="flex gap-2">
                <button 
                  onClick={() => {setSex('female'); if (sex === 'male') setIsPregnant(false);}}
                  className={`flex-1 py-3 rounded-xl border text-xs font-bold transition-all ${sex === 'female' ? 'bg-[#3d2b1f] text-white border-[#3d2b1f] shadow-md' : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}
                >
                  FEMENINO
                </button>
                <button 
                  onClick={() => {setSex('male'); setIsPregnant(false);}}
                  className={`flex-1 py-3 rounded-xl border text-xs font-bold transition-all ${sex === 'male' ? 'bg-[#3d2b1f] text-white border-[#3d2b1f] shadow-md' : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}
                >
                  MASCULINO
                </button>
              </div>
            </div>

            {/* Entrada: Embarazo (Solo visible para mujeres) */}
            <div className={`space-y-2 transition-opacity ${sex === 'male' ? 'opacity-20 pointer-events-none' : 'opacity-100'}`}>
              <label className="flex items-center text-[10px] font-bold text-stone-500 gap-2 uppercase tracking-widest">
                <Baby size={14} /> ¿En Embarazo?
              </label>
              <div className="flex gap-2">
                <button 
                  onClick={() => setIsPregnant(true)}
                  className={`flex-1 py-3 rounded-xl border text-xs font-bold transition-all ${isPregnant ? 'bg-[#d4a373] text-white border-[#d4a373] shadow-md' : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}
                >
                  SÍ
                </button>
                <button 
                  onClick={() => setIsPregnant(false)}
                  className={`flex-1 py-3 rounded-xl border text-xs font-bold transition-all ${!isPregnant ? 'bg-stone-100 text-stone-400 border-stone-200' : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}
                >
                  NO
                </button>
              </div>
            </div>

            {/* Selector de Método de Café */}
            <div className="space-y-2 md:col-span-2">
              <label className="flex items-center text-[10px] font-bold text-stone-500 gap-2 uppercase tracking-widest">
                <Coffee size={14} /> Método de Preparación
              </label>
              <div className="relative">
                <select 
                  value={coffeeType} 
                  onChange={(e) => setCoffeeType(e.target.value)}
                  className="w-full p-4 bg-stone-50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-[#d4a373] outline-none transition-all cursor-pointer appearance-none font-semibold text-stone-700"
                >
                  {Object.keys(coffeeData).map((key) => (
                    <option key={key} value={key}>{coffeeData[key].name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" size={18} />
              </div>
            </div>
          </div>

          {/* Panel de Resultado */}
          {result && (
            <div className="mt-10 p-10 bg-[#fdfaf6] border-2 border-[#d4a373]/30 rounded-[2.5rem] text-center space-y-4 shadow-inner">
              <p className="text-stone-400 uppercase tracking-[0.3em] text-[10px] font-bold">Consumo Seguro Recomendado</p>
              <div className="flex items-center justify-center gap-4">
                <span className="text-8xl font-black text-[#3d2b1f] tracking-tighter leading-none">{result.cups}</span>
                <div className="text-left">
                  <p className="text-2xl font-bold text-[#3d2b1f] leading-none text-nowrap">TAZAS</p>
                  <p className="text-[#d4a373] font-medium text-xs tracking-widest uppercase italic">Ideales</p>
                </div>
              </div>
              <div className="pt-6 border-t border-stone-200/60">
                <p className="text-xs text-stone-500 font-medium">
                  Límite metabólico estimado: <span className="font-bold text-[#3d2b1f]">{result.dailyLimit} mg</span> de cafeína diarios.
                </p>
              </div>
            </div>
          )}

          <div className="w-full text-center">
            <a 
              href="https://60granos.coffee" 
              className="inline-block w-full py-4 bg-[#3d2b1f] text-white rounded-2xl font-bold uppercase tracking-widest text-[10px] hover:bg-[#2a1e15] transition-all text-center shadow-md mb-4"
            >
              Volver al sitio principal
            </a>
          </div>

          {/* Advertencia de Salud */}
          <div className="bg-amber-50/50 rounded-2xl p-6 flex gap-4 items-start border border-amber-100/50 ">
            <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={18} />
            <div className="text-[10px] leading-relaxed text-amber-900/60 font-medium">
              Nota de salud: Estos valores son estimaciones basadas en guías internacionales de salud. La sensibilidad individual puede variar significativamente por factores genéticos. Si experimentas taquicardia o ansiedad, reduce tu ingesta de inmediato.
            </div>
          </div>

          <footer className="text-center pt-4 opacity-40">
            <p className="text-[9px] uppercase tracking-[0.4em] font-medium">
              © 60 GRANOS · PRECISIÓN EN CADA TAZA
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default App;